import { AssociatedTestType } from 'Components/Worksheets/Core/WorksheetAssociatedTestSection/types';
import React from 'react';
import { useSelector } from 'react-redux';
import { WorksheetAssociatedTestSection } from '../../../../../Components/Worksheets/Core';
import { AdditionalVisualDescription } from '../../../../../Components/Worksheets/Core/Fields';
import { IWorksheetComponent } from '../../../../../Components/Worksheets/Core/types';
import { Guid } from '../../../../../Lib/Guid';
import { Field, NumericInput, RadioGroup, TextOutput } from '../../../../Core';
import { NamePrefixModifier, withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { ListFieldSection, PageSection } from '../../../../Layout';
import { PreparationMethod } from '../../../PsdSieveAnalysisWorksheet/types';
import { ParticleDensityDeterminationMethod } from '../../types';

let SpecimenPreparationPageSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const particleDensityDeterminationMethod = useSelector(
    createWorksheetSelector!('HydrometerPortion.Preparation.ParticleDensityDeterminationMethod')
  );
  const importedMoistureContentId: Guid = useSelector(
    createWorksheetSelector!('HydrometerPortion.Preparation.AssociatedMoistureContentTest.ImportedTest.Id')
  );
  const importedSieveAnalysisTestId: Guid = useSelector(
    createWorksheetSelector!('Preparation.AssociatedSieveAnalysisTest.ImportedTest.Id')
  );
  return (
    <PageSection heading="Hydrometer Specimen Preparation">
      <NamePrefixModifier appendNamePrefix="Preparation">
        <ListFieldSection>
          <AdditionalVisualDescription editable />
          <Field input={<RadioGroup />} name="TestStandardAndMethod" />
        </ListFieldSection>
      </NamePrefixModifier>
      <NamePrefixModifier appendNamePrefix="HydrometerPortion.Preparation">
        <ListFieldSection>
          <Field
            input={<RadioGroup />}
            name="ParticleDensityDeterminationMethod"
            options={[
              { label: 'Assumed', value: ParticleDensityDeterminationMethod.Assumed },
              { label: 'Measured', value: ParticleDensityDeterminationMethod.Measured },
            ]}
          />
          <Field
            input={<NumericInput />}
            name="ParticleDensity"
            visible={particleDensityDeterminationMethod === ParticleDensityDeterminationMethod.Assumed}
          />
        </ListFieldSection>
        {particleDensityDeterminationMethod === ParticleDensityDeterminationMethod.Measured && (
          <WorksheetAssociatedTestSection
            heading="Hydrometer Particle Density"
            name="AssociatedParticleDensityTest"
            testType="Particle Density"
            visible={particleDensityDeterminationMethod === ParticleDensityDeterminationMethod.Measured}
            associatedTestModel={{
              external: {
                permitOtherTestMethods: false,
                externalTestResults: (
                  <ListFieldSection>
                    <Field
                      input={<NumericInput />}
                      name="AssociatedParticleDensityTest.ExternalTest.Results.ParticleDensity"
                    />
                  </ListFieldSection>
                ),
              },
            }}
          />
        )}
      </NamePrefixModifier>
      <NamePrefixModifier appendNamePrefix="HydrometerPortion.Preparation">
        <ListFieldSection>
          <Field
            input={<RadioGroup />}
            name="PreparationMethod"
            options={[
              {
                label: 'As received',
                value: PreparationMethod.Moist,
              },
              {
                label: 'Pre-treatment',
                value: PreparationMethod.PreTreated,
              },
            ]}
            confirmText="Changing the material preparation method may clear data. Are you sure you want to continue?"
          />
          <ListFieldSection>
            <Field input={<RadioGroup />} name="GrainSize" />
          </ListFieldSection>
        </ListFieldSection>
      </NamePrefixModifier>

      <NamePrefixModifier appendNamePrefix="HydrometerPortion">
        <WorksheetAssociatedTestSection
          heading="Water Content BS EN ISO 17892-1"
          name="Preparation.AssociatedMoistureContentTest"
          testType="Moisture Content"
          defaultSelection={AssociatedTestType.AutoImported}
          associatedTestModel={{
            measured: true,
            autoImported: true,
            imported: {
              importedTestResults: (
                <ListFieldSection>
                  <Field input={<TextOutput />} name="Masses.MoistureContent" emph />
                </ListFieldSection>
              ),
              importModel: {
                Id: importedMoistureContentId,
              },
            },
            external: {
              permitOtherTestMethods: false,
              externalTestResults: (
                <ListFieldSection>
                  <Field
                    input={<NumericInput />}
                    name="Preparation.AssociatedMoistureContentTest.ExternalTest.Results.MoistureContentPercent"
                  />
                </ListFieldSection>
              ),
            },
          }}
        />
      </NamePrefixModifier>

      <NamePrefixModifier appendNamePrefix="Preparation">
        <WorksheetAssociatedTestSection
          heading="Particle Size Distribution by Sieving Method BS EN ISO 17892-4 Clause 5.2"
          name="AssociatedSieveAnalysisTest"
          testType="Sieve Analysis"
          associatedTestModel={{
            autoImported: true,
            imported: {
              importedTestResults: (
                <ListFieldSection>
                  <Field input={<TextOutput />} name="Sieve_2_0" emph />
                  <Field input={<TextOutput />} name="Sieve_0_063" emph />
                </ListFieldSection>
              ),
              importModel: {
                Id: importedSieveAnalysisTestId,
              },
            },
          }}
        />
      </NamePrefixModifier>
    </PageSection>
  );
};

SpecimenPreparationPageSection = withInjectedProps(SpecimenPreparationPageSection);

export { SpecimenPreparationPageSection };
export default SpecimenPreparationPageSection;
